import type { MouseEvent, ReactNode } from 'react';

export interface LayoutMarkerGlyphProps {
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  label: ReactNode;
  selected?: boolean;
  fill: string;
  labelFill?: string;
  haloFill?: string;
  haloOpacity?: number;
  showDeleteControl?: boolean;
  onDelete?: (event: MouseEvent<SVGCircleElement>) => void;
}

/**
 * Visual marker primitive. Host applications retain marker visibility,
 * selection, dragging, heat-map, tooltip, and deletion policy.
 */
export const LayoutMarkerGlyph = ({
  x,
  y,
  width,
  height,
  fontSize,
  label,
  selected = false,
  fill,
  labelFill = 'white',
  haloFill,
  haloOpacity = 0.22,
  showDeleteControl = false,
  onDelete,
}: LayoutMarkerGlyphProps) => (
  <>
    {!selected && haloFill && (
      <rect
        x={x - width / 2 - 4}
        y={y - height / 2 - 4}
        width={width + 8}
        height={height + 8}
        rx="12"
        ry="12"
        fill={haloFill}
        opacity={haloOpacity}
        style={{ pointerEvents: 'none' }}
      />
    )}
    <rect
      x={x - width / 2}
      y={y - height / 2}
      width={width}
      height={height}
      fill={selected ? '#4477ff' : fill}
      stroke={selected ? '#1e40af' : 'rgba(255,255,255,0.88)'}
      strokeWidth={selected ? 2 : 1.5}
      rx="5"
      ry="5"
      style={{ pointerEvents: 'auto' }}
    />
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="middle"
      fill={selected ? 'white' : labelFill}
      fontSize={fontSize}
      style={{ pointerEvents: 'none', userSelect: 'none' }}
    >
      {label}
    </text>
    {showDeleteControl && (
      <g
        className="delete-button"
        style={{ opacity: 0 }}
        onMouseEnter={(event) => {
          event.currentTarget.style.opacity = '1';
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.opacity = '0';
        }}
      >
        <circle
          cx={x + width / 2}
          cy={y - height / 2}
          r="8"
          fill="red"
          style={{ cursor: 'pointer' }}
          onClick={onDelete}
        />
        <text
          x={x + width / 2}
          y={y - height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontSize="10"
          fontWeight="bold"
          style={{ pointerEvents: 'none' }}
        >
          ×
        </text>
      </g>
    )}
  </>
);
